import type { Values } from './types';

// A dialogue choice jumps to a page by its position in NPCDialogueSO.pages (targetPageIndex; -1 closes the
// dialogue), so moving or deleting a page has to take those jumps along.

export const DIALOGUE_CLASS = 'EnyaRPG.NPC.NPCDialogueSO';

/** The dialogue's list of pages, which choices point at by index. */
export function isPageList(f: { owner?: string; name: string }): boolean {
  return f.owner === DIALOGUE_CLASS && f.name === 'pages';
}

/**
 * The pages after a move or delete, with every choice still jumping to the same page. `order[i]` is the old
 * index of the page now at i. A jump to a deleted page closes the dialogue (-1) instead; one that already
 * pointed past the last page is left as it is.
 */
export function retargetChoices(pages: unknown[], order: number[], oldCount: number): unknown[] {
  const moved = new Map(order.map((old, i) => [old, i]));
  return pages.map((page) => {
    const p = page as Values | null;
    if (!p || !Array.isArray(p.choices)) return page;
    let changed = false;
    const choices = p.choices.map((choice) => {
      const c = choice as Values | null;
      const target = c?.targetPageIndex;
      if (!c || typeof target !== 'number' || target < 0 || target >= oldCount) return choice;
      const to = moved.get(target) ?? -1;
      if (to === target) return choice;
      changed = true;
      return { ...c, targetPageIndex: to };
    });
    return changed ? { ...p, choices } : page;
  });
}

/** How many choices on other pages jump to page `index`. */
export function choicesTo(pages: unknown[], index: number): number {
  let n = 0;
  pages.forEach((page, i) => {
    const choices = (page as Values | null)?.choices;
    if (i === index || !Array.isArray(choices)) return;
    for (const c of choices) if ((c as Values | null)?.targetPageIndex === index) n++;
  });
  return n;
}
